import { PaymentMethodConditions } from '@nemuru-eng/checkout-js';
import {
  getLoanApplicationsByLoanId,
  Loan,
  ProcessingLenderStrategyType,
  ProcessingMode,
} from '@nemuru-eng/js-api-client';
import {
  ChooseStrategyType,
  postLoanApplicationInLender,
  postManualChooseByLoanApplicationId,
} from '@nemuru-eng/js-api-client/slim';

import { LoanApplicationEntity } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/_shared/components/ProcessingStrategy/entities/LoanApplication/LoanApplicationEntity';
import { AbstractLoanPaymentMethodDetailEntity } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/_shared/entities/PaymentMethodDetail/AbstractLoanPaymentMethodDetailEntity';

export abstract class AbstractProcessingStrategyEntity {
  protected readonly _paymentMethodDetailEntity: AbstractLoanPaymentMethodDetailEntity;
  protected _loanApplications: LoanApplicationEntity[] | undefined;
  protected _paymentMethodConditions: PaymentMethodConditions[] | undefined;

  constructor(paymentMethodDetailEntity: AbstractLoanPaymentMethodDetailEntity) {
    this._paymentMethodDetailEntity = paymentMethodDetailEntity;
  }

  public get loan(): Loan | undefined {
    return this._paymentMethodDetailEntity.loan;
  }

  public abstract allowProcessing(loanApplicationId: string): boolean;
  protected abstract sortLoanApplications(loanApplications: LoanApplicationEntity[]): LoanApplicationEntity[];
  protected abstract setProcessingStatus(loanApplications: LoanApplicationEntity[]): LoanApplicationEntity[];
  public abstract clone(): this;

  public isBorrowerSelection(): boolean {
    return this.chooseStrategyType === ChooseStrategyType.BORROWER_SELECTION;
  }

  public isManual(): boolean {
    return this.chooseStrategyType === ChooseStrategyType.MANUAL;
  }

  public isManualProcessingMode(): boolean {
    return this._paymentMethodDetailEntity.processingStrategy?.details?.processingMode === ProcessingMode.MANUAL;
  }

  public allowChoosing(loanApplicationId: string): boolean {
    if (!this._loanApplications) return false;
    // If there's already a selected loan application, we don't allow choosing another one (should be changed in the future)
    const loanApplication = this._loanApplications.find((l) => l.id === loanApplicationId);

    if (!loanApplication) return false;

    if (this._paymentMethodDetailEntity.isSigned()) return false;

    if (loanApplication.isNotProcessed() || loanApplication.hasErrors()) return false;

    if (loanApplication.isNo()) return false;

    // 1. Auto loan (new path)

    if (this._paymentMethodDetailEntity.isAuto()) return !loanApplication.isSelected();

    // 2. Borrower selection (original path)

    if (this.hasSelectedLoanApplication()) return false;

    if (loanApplication.isSelected()) return false;

    const allProcessed = this._loanApplications.every((l) => l.isProcessedOrSelected());

    return allProcessed && this.isBorrowerSelection();
  }

  public setPaymentMethodConditions(): this {
    const newPaymentMethodDetailEntity = this._paymentMethodDetailEntity.clone();
    newPaymentMethodDetailEntity.setPaymentMethodFromCalculator();
    this._paymentMethodConditions = newPaymentMethodDetailEntity.paymentMethod?.paymentMethodConditions ?? undefined;
    return this;
  }

  public async getLoanApplications(): Promise<this> {
    const loanId = this._paymentMethodDetailEntity.paymentMethodId;

    if (!loanId) {
      return this;
    }

    const loanApplications = await getLoanApplicationsByLoanId({ loanId });
    const loanApplicationEntities = loanApplications.map((l) => new LoanApplicationEntity(l));
    const sortedLoanApplications = this.sortLoanApplications(loanApplicationEntities);
    const processedLoanApplications = this.setProcessingStatus(sortedLoanApplications);
    this._loanApplications = processedLoanApplications;

    return this;
  }

  public sortAndSetProcessingStatus(): this {
    if (!this._loanApplications) {
      return this;
    }

    const sortedLoanApplications = this.sortLoanApplications(this._loanApplications);
    const processedLoanApplications = this.setProcessingStatus(sortedLoanApplications);
    this._loanApplications = processedLoanApplications;
    return this;
  }

  public async processLoanApplication(loanApplicationId: string): Promise<this> {
    if (!this._loanApplications) {
      return this;
    }

    try {
      await postLoanApplicationInLender({ loanApplicationId });
      if (this.isBorrowerSelection()) {
        await this.waitForProcessedOrSelectedApplication(loanApplicationId);
      } else if (this.isManual()) {
        await this.waitForProcessedOrSelectedApplication(loanApplicationId);
        // Since in the manual process, the former selected loan application keeps selected.
        // And in order to avoid the race condition that we request the loan application while the choose is undergoing,
        // we wait for 5 seconds to let the choose select the loan application once the manually processed application has been processed.
        await new Promise((resolve) => setTimeout(resolve, 5000));
      } else {
        await this.waitForSelectedApplication();
      }
      await this.getLoanApplications();
      return this;
    } catch (error) {
      console.error(error);
      return this;
    }
  }

  public async chooseLoanApplication(loanApplicationId: string): Promise<this> {
    const loanId = this._paymentMethodDetailEntity.paymentMethodId;

    if (!loanId) {
      throw new Error('Payment method id is missing');
    }

    try {
      await postManualChooseByLoanApplicationId({ loanId, loanApplicationId });
      await this.waitForSelectedApplication();
      await this.getLoanApplications();
      return this;
    } catch (error) {
      console.error(error);
      return this;
    }
  }

  private async waitForSelectedApplication() {
    const loanId = this._paymentMethodDetailEntity.paymentMethodId;

    if (!loanId) {
      throw new Error('Payment method id is missing to wait for selected application');
    }

    if (!this._loanApplications) {
      throw new Error('Loan applications are missing to wait for selected application');
    }

    await this._paymentMethodDetailEntity.waitUntilSelectedApplication();
  }

  private async waitForProcessedOrSelectedApplication(loanApplicationId: string) {
    const loanApplication = this._loanApplications?.find((l) => l.id === loanApplicationId);
    if (!loanApplication) {
      throw new Error('Loan application not found to wait for processed or selected');
    }
    await loanApplication.waitUntilProcessedOrSelected();
  }

  public getSelectedLoanApplication(): LoanApplicationEntity | undefined {
    return this._loanApplications?.find((loanApplication) => loanApplication.isSelected());
  }

  public hasSelectedLoanApplication(): boolean {
    return this._loanApplications?.some((loanApplication) => loanApplication.isSelected()) ?? false;
  }

  public get chooseStrategyType(): ChooseStrategyType | undefined {
    // FIXME: This should be changed in backend (Let's hard-code this for now)
    if (this._paymentMethodDetailEntity.isCreditLine()) {
      return ChooseStrategyType.BORROWER_SELECTION;
    }
    return this._paymentMethodDetailEntity.processingStrategy?.details?.orderBy;
  }

  public get strategyType(): ProcessingLenderStrategyType {
    return this._paymentMethodDetailEntity.processingStrategy?.type ?? ProcessingLenderStrategyType.PARALLEL;
  }

  public get paymentMethodConditions(): PaymentMethodConditions[] | undefined {
    const strategy = this._paymentMethodDetailEntity.processingStrategy;

    // Filter payment method conditions by lender name and annual interest
    if (strategy?.details?.orderBy === ChooseStrategyType.MANUAL && strategy.details.options) {
      const options = strategy.details.options;

      const filteredConditions = this._paymentMethodConditions?.filter((condition) => {
        const lenderName = condition.conditions?.lenderName;
        const annualInterest = condition.conditions?.annualInterest?.value;
        return options.some((option) => option.lenderName === lenderName && option.annualInterest === annualInterest);
      });

      return filteredConditions;
    }
    return this._paymentMethodConditions;
  }

  public get loanApplications(): LoanApplicationEntity[] | undefined {
    return this._loanApplications;
  }

  public set paymentMethodConditions(paymentMethodConditions: PaymentMethodConditions[] | undefined) {
    this._paymentMethodConditions = paymentMethodConditions;
  }
}
